import React, { Suspense, lazy } from 'react';
import { useDispatch } from 'react-redux';
import { link_click } from '../redux/actions';
import NumberOfUnreadMessages from '../Elements/NumberOfUnreadMessages';
import NumberOfUndownloadedFiles from '../Elements/NumberOfUndownloadedFiles';
import menuitems from '../menuitems';
import useTheme from '../Helpers/useTheme';

// Static string-literal imports => each its own chunk, no import-context sweep.
const Profile = lazy(() => import('../Contents/User/UserAdmin/Profile'));
const Messages = lazy(() => import('../Contents/Messages/Messages'));
const Downloads = lazy(() => import('../Contents/Downloads/Downloads'));

// The page-header eyebrow (section) is derived from the active top-level menu group.
// Pass this group object on link_click so the in-app nav path shows "ACCOUNT" too.
const accountSection = menuitems.find((m) => m.text === 'Account');

// Shared profile popover, rendered once by BravoSidebar and controlled via `open`.
// Two triggers toggle it (rail avatar / expanded user-chip). `shifted` moves it
// right (left:264px) so it clears the pinned 256px sidebar.
export default function ProfileMenuPopover({
  open,
  onClose,
  onNavigate,
  shifted,
  loggedInUser,
  loggedInUsersNotInUse,
  switchToUserClick,
  handleSwitchUserClick,
  handleLogoutClick,
}) {
  const dispatch = useDispatch();
  const { isDark, toggle } = useTheme();
  if (!open || !loggedInUser) return null;

  const afterAction = onNavigate || onClose;
  const fullName = `${loggedInUser.firstName} ${loggedInUser.lastName}`;
  const initials = `${(loggedInUser.firstName || ' ')[0]}${(loggedInUser.lastName || ' ')[0]}`.trim().toUpperCase();

  const openPage = (Comp, title, icon, url, menuitem = null) => {
    dispatch(
      link_click(
        <Suspense fallback={<div />}>
          <Comp />
        </Suspense>,
        title,
        icon,
        menuitem,
        0,
        url
      )
    );
    afterAction();
  };

  return (
    <>
      <div
        className='flyout-catch'
        onClick={onClose}
      />
      <div className={`profile-menu${shifted ? ' shifted' : ''}`}>
        <div className='pm-head'>
          <div className='av'>{initials}</div>
          <div>
            <div className='nm'>{fullName}</div>
            <div className='rl'>{loggedInUser.userName}</div>
          </div>
        </div>
        <div className='pm-list'>
          {(loggedInUsersNotInUse || []).map((user, i) => (
            <button
              type='button'
              key={`switch-${i}`}
              className='pm-item'
              onClick={() => {
                switchToUserClick(user);
                afterAction();
              }}
            >
              <i className='fa-solid fa-user-group' />
              <span>{`Switch to ${user.userName}`}</span>
            </button>
          ))}
          <button
            type='button'
            className='pm-item'
            onClick={() => openPage(Profile, 'Profile', 'fa-solid fa-user-astronaut', 'profile', accountSection)}
          >
            <i className='fa-solid fa-user' />
            <span>{'Profile'}</span>
          </button>
          <button
            type='button'
            className='pm-item'
            onClick={() => openPage(Messages, 'Messages', 'fa-regular fa-envelope', 'messages', accountSection)}
          >
            <i className='fa-regular fa-envelope' />
            <span>{'Message'}</span>
            <NumberOfUnreadMessages showNumber={true} />
          </button>
          <button
            type='button'
            className='pm-item'
            onClick={() => openPage(Downloads, 'Downloads', 'fa-solid fa-download', 'downloads', accountSection)}
          >
            <i className='fa-solid fa-download' />
            <span>{'Downloads'}</span>
            <NumberOfUndownloadedFiles showNumber={true} />
          </button>
          <button
            type='button'
            className='pm-item pm-theme'
            role='switch'
            aria-checked={isDark}
            onClick={toggle}
          >
            <i className={isDark ? 'fa-solid fa-moon' : 'fa-regular fa-moon'} />
            <span>{'Dark mode'}</span>
            <span
              className={'pm-switch' + (isDark ? ' on' : '')}
              aria-hidden='true'
            >
              <span className='pm-knob' />
            </span>
          </button>
          <button
            type='button'
            className='pm-item'
            onClick={() => {
              handleSwitchUserClick();
              afterAction();
            }}
          >
            <i className='fa-solid fa-right-left' />
            <span>{'Switch Azure User'}</span>
          </button>
          <button
            type='button'
            className='pm-item danger'
            onClick={() => {
              handleLogoutClick();
              afterAction();
            }}
          >
            <i className='fa-solid fa-power-off' />
            <span>{'Logout'}</span>
          </button>
        </div>
      </div>
    </>
  );
}
